import { isAxiosError } from 'axios';
import { doc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { GeneratedCV } from '../components/TenderDetails/GenerateCVsSection';
import type { LoadingStates, SupportDoc, UploadedAt } from '../components/TenderDetails/types';
import { getLoadingKey } from '../components/TenderDetails/utils';
import { apiService, generateCVs } from '../services/api';
import { firestore } from '../services/firebase';
import type { User } from '../types/auth';
import type { Tender } from '../types/tender';
import { logAIActivity, logDocumentActivity } from '../utils/activityLogger';
import { validateTenderUpload } from '../utils/storageValidation';

export interface UseTenderCVsProps {
  tenderId: string | undefined;
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  allCVs: SupportDoc[];
  tenderCVs: SupportDoc[];
  setTenderCVs: React.Dispatch<React.SetStateAction<SupportDoc[]>>;
  setLoadingStates: React.Dispatch<React.SetStateAction<LoadingStates>>;
  setProcessingFiles: React.Dispatch<
    React.SetStateAction<{
      support: string[];
      review: string[];
      form: string[];
      partner_doc: string[];
    }>
  >;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  refreshUserDetails: (silent?: boolean) => void | Promise<void>;
}

export const useTenderCVs = ({
  tenderId,
  tender,
  account,
  allCVs,
  tenderCVs,
  setTenderCVs,
  setLoadingStates,
  setProcessingFiles,
  showSnackbar,
  refreshUserDetails,
}: UseTenderCVsProps) => {
  const [cvGenerationStatus, setCvGenerationStatus] = useState<string>('idle');
  const [cvServiceStartedAt, setCvServiceStartedAt] = useState<UploadedAt | undefined>(undefined);
  const [generatedCVs, setGeneratedCVs] = useState<GeneratedCV[]>([]);
  const [isDraggingCV, setIsDraggingCV] = useState(false);

  useEffect(() => {
    if (!account?.uid || !tenderId) {
      return;
    }

    const cvServiceRef = doc(firestore, 'tenders', tenderId, 'services', 'cvGeneration');

    const unsubscribe = onSnapshot(cvServiceRef, (docSnap) => {
      if (!docSnap.exists()) {
        return;
      }

      const data = docSnap.data();
      if (data.status) {
        setCvGenerationStatus(data.status);

        // Clear optimistic 'initializing'/'processing' states when the service finishes
        if (
          data.status === 'success' ||
          data.status === 'complete' ||
          data.status === 'error' ||
          data.status === 'idle' ||
          data.status === 'CVs Complete'
        ) {
          setTenderCVs((prev) => {
            let changed = false;
            const updated = prev.map((cv) => {
              if (cv.generationStatus === 'initializing' || cv.generationStatus === 'processing') {
                changed = true;
                return {
                  ...cv,
                  generationStatus: data.status === 'error' ? 'error' : 'success',
                } as SupportDoc;
              }
              return cv;
            });
            return changed ? updated : prev;
          });
        }
      }
      setCvServiceStartedAt(data.startedAt || undefined);

      if (!data.uploaded_uris || !Array.isArray(data.uploaded_uris)) {
        return;
      }

      const generatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date();
      const cvs = data.uploaded_uris.map((uri: string) => ({
        name: uri.split('/').pop() || 'Generated CV',
        uri,
        generatedAt,
      }));
      setGeneratedCVs(cvs);
    });

    return () => unsubscribe();
  }, [tenderId, account?.uid, setTenderCVs]);

  const handleGenerateCVs = async () => {
    if (!account?.uid || !tenderId || !account.clientId) {
      showSnackbar('Missing critical information.', 'error');
      return;
    }

    const cvFilenames = allCVs
      .map((doc) => doc.filename || doc.name || doc.originalFilename)
      .filter((name): name is string => !!name);

    if (cvFilenames.length === 0) {
      showSnackbar('No CVs uploaded to generate.', 'warning');
      return;
    }

    try {
      setCvGenerationStatus('initializing');
      setCvServiceStartedAt(new Date());

      // Create transient documents for any CVs that don't have one yet to allow tracking
      const missingDocs = allCVs.filter(
        (cv) => !tenderCVs.some((tCv) => tCv.originalFilename === cv.originalFilename)
      );

      if (missingDocs.length > 0) {
        const promises = missingDocs.map(async (cv) => {
          if (cv.originalFilename) {
            const newCvRef = doc(firestore, 'tenders', tenderId, 'library', cv.originalFilename);
            const cleanSupportDoc = Object.fromEntries(
              Object.entries(cv).filter(([k, v]) => v !== undefined && k !== 'isLibraryItem')
            );
            return setDoc(
              newCvRef,
              {
                ...cleanSupportDoc,
                id: cv.originalFilename,
                generationStatus: 'initializing',
                startedAt: new Date().toISOString(),
                doc_type: 'cv',
              },
              { merge: true }
            );
          }
        });
        await Promise.all(promises);
      }

      const jobId = crypto.randomUUID();
      await generateCVs({
        userId: account.uid,
        tenderId,
        clientId: account.clientId,
        cv_files: cvFilenames,
        jobId,
      });
      showSnackbar('CV generation started.', 'success');
      logAIActivity.serviceUsed({
        serviceName: 'CV Generator',
        serviceType: 'cv_generation',
        inputType: 'multiple_files',
        success: true,
      });
      refreshUserDetails(true);
    } catch (error) {
      let message = error instanceof Error ? error.message : 'Unknown error';
      if (isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) {
          message = data.message;
        }
      }
      showSnackbar(`Failed to start CV generation: ${message}`, 'error');
      setCvGenerationStatus('error');
    }
  };

  const handleGenerateSingleCV = async (supportDoc: SupportDoc) => {
    if (!account?.uid || !tenderId || !account.clientId || !supportDoc.originalFilename) {
      showSnackbar('Missing critical information.', 'error');
      return;
    }

    try {
      const isTransient = !tenderCVs.some(
        (cv) => cv.originalFilename === supportDoc.originalFilename
      );

      if (isTransient) {
        const newCvRef = doc(
          firestore,
          'tenders',
          tenderId,
          'library',
          supportDoc.originalFilename
        );

        const cleanSupportDoc = Object.fromEntries(
          Object.entries(supportDoc).filter(([k, v]) => v !== undefined && k !== 'isLibraryItem')
        );

        await setDoc(
          newCvRef,
          {
            ...cleanSupportDoc,
            id: supportDoc.originalFilename,
            generationStatus: 'initializing',
            startedAt: new Date().toISOString(),
            doc_type: 'cv',
          },
          { merge: true }
        );
      }

      setTenderCVs((prev) => {
        const existingIndex = prev.findIndex(
          (cv) => cv.originalFilename === supportDoc.originalFilename
        );
        if (existingIndex >= 0) {
          const newCvs = [...prev];
          newCvs[existingIndex] = {
            ...newCvs[existingIndex],
            generationStatus: 'initializing',
            startedAt: new Date().toISOString(),
          };
          return newCvs;
        } else {
          return [
            ...prev,
            {
              ...supportDoc,
              generationStatus: 'initializing',
              startedAt: new Date().toISOString(),
            },
          ];
        }
      });

      const jobId = crypto.randomUUID();
      const payload = {
        userId: account.uid,
        tenderId,
        clientId: account.clientId,
        cv_files: [supportDoc.filename || supportDoc.name || supportDoc.originalFilename],
        jobId,
      };

      setCvGenerationStatus('initializing');
      setCvServiceStartedAt(new Date());

      await generateCVs(payload);
      showSnackbar(`CV generation started for ${supportDoc.originalFilename}`, 'success');
      logAIActivity.serviceUsed({
        serviceName: 'CV Generator',
        serviceType: 'cv_generation',
        inputType: 'single_file',
        success: true,
      });
      refreshUserDetails(true);
    } catch (error) {
      let message = error instanceof Error ? error.message : 'Unknown error';
      if (isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) {
          message = data.message;
        }
      }
      showSnackbar(`Failed to start CV generation: ${message}`, 'error');

      setTenderCVs((prev) =>
        prev.map((cv) =>
          cv.originalFilename === supportDoc.originalFilename
            ? { ...cv, generationStatus: 'idle' }
            : cv
        )
      );

      try {
        const docId = supportDoc.originalFilename;
        if (docId) {
          const cvDocRef = doc(firestore, 'tenders', tenderId, 'library', docId);
          setDoc(cvDocRef, { generationStatus: 'idle' }, { merge: true }).catch(() => {
            /* ignore */
          });
        }
      } catch (_e) {
        /* ignore */
      }
    }
  };

  const handleCVUpload = async (file: File) => {
    if (
      !tender?.id ||
      !file ||
      !account?.uid ||
      !account?.clientId ||
      !account?.actualDefaultBucketName
    ) {
      showSnackbar(
        'Missing critical information for CV upload (user, client, tender, or GCS config).',
        'error'
      );
      return;
    }

    try {
      const validation = await validateTenderUpload(
        account.uid,
        tender.id,
        account.clientId,
        [file],
        'cv'
      );
      if (!validation.allowed) {
        showSnackbar(validation.reason || 'Upload limit exceeded', 'error', 5000);
        return;
      }
    } catch (_validationError) {
      /* ignore */
    }

    const loadingKey = getLoadingKey(tender.id, 'upload', 'cv');
    setLoadingStates((prev: LoadingStates) => ({ ...prev, [loadingKey]: true }));
    setProcessingFiles((prev) => ({
      ...prev,
      support: [...prev.support, file.name],
    }));

    try {
      const response = await apiService.uploadTenderCV({
        userId: account.uid,
        tenderId: tender.id,
        file,
        clientId: account.clientId,
        actualBucketName: account.actualDefaultBucketName,
        docType: 'cv',
      });

      if (response?.success) {
        logDocumentActivity.uploaded({
          type: 'cv',
          fileName: file.name,
          fileSize: file.size,
          location: `tender/${tender.id}/cvs`,
        });
        showSnackbar('CV uploaded successfully!', 'success');
      } else {
        throw new Error(response.message || 'CV upload failed.');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showSnackbar(`Failed to upload CV: ${message}`, 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
      setProcessingFiles((prev) => ({
        ...prev,
        support: prev.support.filter((f) => f !== file.name),
      }));
    }
  };

  const commonDragEvents = (e: React.DragEvent, setDraggingState: (s: boolean) => void) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDraggingState(true);
    } else if (e.type === 'dragleave') {
      setDraggingState(false);
    }
  };

  const handleDragEventsCV = (e: React.DragEvent) => commonDragEvents(e, setIsDraggingCV);

  const handleDropCV = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCV(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      for (const file of Array.from(e.dataTransfer.files)) {
        handleCVUpload(file);
      }
    }
  };

  const handleResetCVService = async () => {
    if (!account?.uid || !tenderId) {
      return;
    }
    try {
      try {
        await apiService.reportError({
          tenderId: tenderId,
          error: cvGenerationStatus || 'Unknown CV Generation Error',
          component: 'CV Generation - Service Reset',
        });
      } catch (_e) {
        // Ignored
      }

      const serviceRef = doc(firestore, 'tenders', tenderId, 'services', 'cvGeneration');
      await updateDoc(serviceRef, { status: 'error' });

      const resetPromises = tenderCVs.map(async (cv) => {
        const docId = (cv as SupportDoc & { id?: string }).id || cv.originalFilename;
        if (docId) {
          const cvDocRef = doc(firestore, 'tenders', tenderId, 'library', docId);
          await setDoc(cvDocRef, { generationStatus: 'error' }, { merge: true });
        }
      });

      await Promise.all(resetPromises);
      showSnackbar('CV Generation service and all files reset. Admin notified.', 'success');
    } catch (_e) {
      showSnackbar('Failed to reset CV service', 'error');
    }
  };

  const handleResetSingleCV = async (supportDoc: SupportDoc) => {
    if (!account?.uid || !tenderId) {
      return;
    }
    try {
      try {
        await apiService.reportError({
          tenderId: tenderId,
          error: supportDoc.generationStatus || cvGenerationStatus || 'Unknown CV Error',
          component: `CV Generation - Single Reset (${supportDoc.originalFilename || 'Unknown File'})`,
        });
      } catch (_e) {
        // Ignored
      }

      const docId = (supportDoc as SupportDoc & { id?: string }).id || supportDoc.originalFilename;
      if (docId) {
        const cvDocRef = doc(firestore, 'tenders', tenderId, 'library', docId);
        await setDoc(cvDocRef, { generationStatus: 'error' }, { merge: true });
        showSnackbar(`Reset status for ${supportDoc.originalFilename}. Admin notified.`, 'success');
      }
    } catch (_e) {
      showSnackbar('Failed to reset CV status', 'error');
    }
  };

  const handleReportSingleCVError = async (supportDoc: SupportDoc) => {
    if (!account?.uid || !tenderId) {
      return;
    }
    try {
      await apiService.reportError({
        tenderId: tenderId,
        error: supportDoc.generationStatus || cvGenerationStatus || 'Unknown CV Error',
        component: `CV Generation - User Reported (${supportDoc.originalFilename || 'Unknown File'})`,
      });
      showSnackbar('Issue reported successfully. Our team has been notified.', 'success');
    } catch (_e) {
      showSnackbar('Failed to report issue', 'error');
    }
  };

  return {
    cvGenerationStatus,
    cvServiceStartedAt,
    generatedCVs,
    isDraggingCV,
    handleGenerateCVs,
    handleGenerateSingleCV,
    handleCVUpload,
    handleDragEventsCV,
    handleDropCV,
    handleResetCVService,
    handleResetSingleCV,
    handleReportSingleCVError,
  };
};
